/**
 * Cohort Activity Management API
 */

import { apiClient, ApiResponse, ApiError, ApiErrorResponse } from './client';

/**
 * Activity type values — single source of truth.
 * Used to derive the TypeScript union and the Select options list.
 */
export const ACTIVITY_TYPES = [
  'Training',
  'Reading',
  'Workshop',
  'Project',
  'Certification',
  'Checkride',
  'Amazon Learn',
  'TFC Hub'
] as const;

export type ActivityType = (typeof ACTIVITY_TYPES)[number];

/** Cloudscape Select-compatible options derived from ACTIVITY_TYPES */
export const ACTIVITY_TYPE_OPTIONS = ACTIVITY_TYPES.map((t) => ({ label: t, value: t }));

export interface CohortActivity {
  activityId: string;
  cohortId: string;
  programId: string;
  activityType: ActivityType;
  title: string;
  description: string;
  linkUrl?: string;
  requiresMentorApproval: boolean;
  requiredForGraduation?: boolean;
  week?: number;
  order: number;
  createdBy: string;
  createdAt: string;
}

export interface CreateActivityRequest {
  activityType: ActivityType;
  title: string;
  description: string;
  linkUrl?: string;
  requiresMentorApproval: boolean;
  requiredForGraduation?: boolean;
  week?: number;
}

export interface UpdateActivityRequest {
  activityType?: ActivityType;
  title?: string;
  description?: string;
  linkUrl?: string;
  requiresMentorApproval?: boolean;
  requiredForGraduation?: boolean;
  week?: number | null;
  order?: number;
}

export interface ActivityCompletion {
  activityId: string;
  status: 'completed' | 'pending_approval';
  completedAt: string;
  submittedAt: string;
  deliverableName?: string;
  deliverableLink?: string;
  recordingLink?: string;
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  approvedBy?: string;
  approvedAt?: string;
}

export interface ActivityProgressStat {
  activityId: string;
  title: string;
  activityType: ActivityType;
  week?: number;
  order?: number;
  requiredForGraduation: boolean;
  requiresMentorApproval: boolean;
  completionCount: number;
  pendingCount: number;
  completionRate: number; // 0–100
}

export interface CohortProgress {
  cohortId: string;
  enrollment: {
    totalEnrolled: number;
    totalGraduated: number;
    graduationRate: number;
    inProgress: number;
    notStarted: number;
  };
  mentorCoverage: {
    activeMentors: number;
    assignedMentees: number;
    unassignedParticipants: number;
  };
  pendingApprovals: number;
  activityStats: ActivityProgressStat[];
}

export interface MarkCompleteRequest {
  completedAt: string;
  deliverableName?: string;
  deliverableLink?: string;
  recordingLink?: string;
}

/**
 * Activity API Service
 */
export const activitiesApi = {
  /**
   * List all activities for a cohort
   */
  async list(cohortId: string): Promise<CohortActivity[]> {
    const response = await apiClient.get<ApiResponse<{ activities: CohortActivity[] }>>(
      `/cohorts/${cohortId}/activities`
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data.activities;
  },

  /**
   * Create a new activity
   */
  async create(cohortId: string, data: CreateActivityRequest): Promise<CohortActivity> {
    const response = await apiClient.post<ApiResponse<CohortActivity>>(
      `/cohorts/${cohortId}/activities`,
      data
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data;
  },

  /**
   * Update an activity
   */
  async update(cohortId: string, activityId: string, data: UpdateActivityRequest): Promise<CohortActivity> {
    const response = await apiClient.put<ApiResponse<CohortActivity>>(
      `/cohorts/${cohortId}/activities/${activityId}`,
      data
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data;
  },

  /**
   * Delete an activity
   */
  async delete(cohortId: string, activityId: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/cohorts/${cohortId}/activities/${activityId}`
    );
    if (!response.success) {
      throw new ApiError(response as unknown as ApiErrorResponse);
    }
  },

  /**
   * Reorder activities
   */
  async reorder(cohortId: string, activityOrders: { activityId: string; order: number }[]): Promise<void> {
    const response = await apiClient.put<ApiResponse<void>>(
      `/cohorts/${cohortId}/activities/reorder`,
      { activityOrders }
    );
    if (!response.success) {
      throw new ApiError(response as unknown as ApiErrorResponse);
    }
  },

  /**
   * Mark activity as complete
   */
  async markComplete(cohortId: string, activityId: string, data: MarkCompleteRequest): Promise<{ status: string; requiresApproval: boolean }> {
    const response = await apiClient.post<ApiResponse<{ status: string; requiresApproval: boolean }>>(
      `/cohorts/${cohortId}/activities/${activityId}/complete`,
      data
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data;
  },

  /**
   * Get user's activity completions for a cohort
   */
  async getUserCompletions(cohortId: string, userId: string): Promise<ActivityCompletion[]> {
    const response = await apiClient.get<ApiResponse<{ completions: ActivityCompletion[] }>>(
      `/cohorts/${cohortId}/users/${userId}/completions`
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data.completions;
  },

  /**
   * Approve activity completion (mentor only)
   */
  async approveActivity(cohortId: string, activityId: string, userId: string, notes?: string): Promise<void> {
    const response = await apiClient.post<ApiResponse<void>>(
      `/cohorts/${cohortId}/activities/${activityId}/approve`,
      { userId, notes }
    );
    if (!response.success) {
      throw new ApiError(response as unknown as ApiErrorResponse);
    }
  },

  /**
   * Get cohort-wide progress stats (admin only)
   */
  async getCohortProgress(cohortId: string): Promise<CohortProgress> {
    const response = await apiClient.get<ApiResponse<CohortProgress>>(
      `/cohorts/${cohortId}/progress`
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse);
    return response.data;
  },
};
